import type { Theme } from '@mui/material';

import { OPERATION_CATALOG, OPERATION_CATEGORIES } from '../../labs/ImageLab/operationCatalog';
import { TEXT_OPERATION_ORDER } from '../../labs/WriterLab/operations/operationCatalog';

/**
 * Static capability facts about each lab, for the dashboard's lab cards.
 *
 * Counts are derived from the shipped catalogs rather than hardcoded, so a new
 * operation shows up on the landing page the moment it's added to the catalog —
 * the same reason `LabsPicker` reads the lab list from the backend instead of a
 * literal. `labId` matches the backend's `/api/v3/labs` ids, which is what the
 * SMA-13 feature gate filters on.
 */
export interface LabCapability {
  labId: string;
  /** Fallback name/description — the backend's own values win when present. */
  fallbackName: string;
  fallbackDescription: string;
  /** Number of operations the lab exposes. */
  operationCount: number;
  /** Short capability words shown as chips on the card. */
  highlights: readonly string[];
  /** Picks the accent color off the active theme, so all three themes work. */
  accent: (theme: Theme) => string;
}

/** Animation Lab's Phase 1 "Motion Bridge" operations (no catalog module yet). */
export const ANIMATION_OPERATIONS = ['ImageToVideo', 'TextToVideo', 'Interpolate', 'Extend'] as const;

export const LAB_CAPABILITIES: readonly LabCapability[] = [
  {
    labId: 'image-lab',
    fallbackName: 'Image Lab',
    fallbackDescription:
      'Generate, retouch, relight and restyle stills on a single canvas.',
    operationCount: Object.keys(OPERATION_CATALOG).length,
    highlights: OPERATION_CATEGORIES,
    accent: theme => theme.palette.primary.main,
  },
  {
    labId: 'writer-lab',
    fallbackName: "Writer's Lab",
    fallbackDescription:
      'Draft, restructure and critique long-form documents with an AI collaborator.',
    operationCount: TEXT_OPERATION_ORDER.length,
    highlights: ['Generate', 'Refine', 'Restructure', 'Critique'],
    accent: theme => theme.palette.secondary.main,
  },
  {
    labId: 'animation-lab',
    fallbackName: 'Animation Lab',
    fallbackDescription: 'Bring stills into motion — image-to-video, interpolation, extension.',
    operationCount: ANIMATION_OPERATIONS.length,
    highlights: ['Motion', 'Interpolate', 'Extend'],
    accent: theme => theme.palette.info?.main ?? theme.palette.primary.light,
  },
] as const;

/** Total operations across every lab — the headline "capabilities" number. */
export const TOTAL_OPERATION_COUNT = LAB_CAPABILITIES.reduce(
  (sum, lab) => sum + lab.operationCount,
  0,
);

/**
 * A theme-native gradient for the hero and card washes.
 *
 * Built from the active theme's own primary/secondary tokens rather than fixed
 * hex values, so `studio` (coral/teal), `smartai` (blue/cyan) and
 * `constellations` (cyan→purple) each render in their own identity, in both
 * light and dark mode.
 */
export const themeGradient = (theme: Theme, angle = 135): string =>
  `linear-gradient(${angle}deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;

/**
 * A low-opacity wash of the same gradient, for surfaces that sit behind text.
 * Dark mode needs a touch more presence to read against the near-black canvas.
 */
export const themeWash = (theme: Theme, angle = 135): string => {
  const alpha = theme.palette.mode === 'dark' ? 0.22 : 0.12;
  return `linear-gradient(${angle}deg, ${hexWithAlpha(theme.palette.primary.main, alpha)} 0%, ${hexWithAlpha(theme.palette.secondary.main, alpha)} 100%)`;
};

/**
 * Appends an alpha channel to a color. Handles the `#rrggbb` form the themes
 * use; anything else (named colors, `rgb()`) is returned untouched so a caller
 * never ends up with an invalid CSS color.
 */
export function hexWithAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return color;
  const clamped = Math.max(0, Math.min(1, alpha));
  const hex = Math.round(clamped * 255).toString(16).padStart(2, '0');
  return `${color}${hex}`;
}
